import React, { FC, createContext, useContext, useMemo } from 'react';
import { useTrainingStore } from '~/training/state';

/*
  Pretest mode for template items. Inside a PretestAnswersProvider, answer
  items record the learner's choice for the drawn question and do nothing
  else: no saveAnswers, no right/wrong reveal, no feedback and no LMS
  interaction. The server grades the choices when the pretest is submitted.
*/

export interface PretestAnswers {
  multiple: boolean;
  // While the answers are on their way to the server, the choices stay as sent.
  disabled: boolean;
  isChosen(optionId: string): boolean;
  choose(optionId: string): void;
}

const PretestAnswersContext = createContext<PretestAnswers | null>(null);

const NO_CHOICE: string[] = [];

export const PretestAnswersProvider: FC<{
  questionId: string;
  // The drawn question's options; any other answer on the slide is ignored.
  optionIds: string[];
  multiple: boolean;
}> = ({ questionId, optionIds, multiple, children }) => {
  const chosen = useTrainingStore(
    s => s.pretestAnswers[questionId] ?? NO_CHOICE
  );
  const choosePretestOption = useTrainingStore(s => s.choosePretestOption);
  const disabled = useTrainingStore(s => s.pretestBusy);

  const value = useMemo<PretestAnswers>(
    () => ({
      multiple,
      disabled,
      isChosen: optionId => chosen.includes(optionId),
      choose: optionId => {
        if (optionIds.includes(optionId)) {
          choosePretestOption(questionId, optionId, multiple);
        }
      }
    }),
    [chosen, choosePretestOption, questionId, optionIds, multiple, disabled]
  );

  return (
    <PretestAnswersContext.Provider value={value}>
      {children}
    </PretestAnswersContext.Provider>
  );
};

/** The pretest answers when rendered inside a pretest question, else null. */
export const usePretestAnswers = () => useContext(PretestAnswersContext);
